import React, { useState } from 'react';
import { Modal, View, TouchableWithoutFeedback, Platform, StyleSheet, Dimensions } from 'react-native';
import DateTimePicker, { useDefaultStyles } from 'react-native-ui-datepicker';
import NativeDateTimePicker from '@react-native-community/datetimepicker';
import { Text, Button, useTheme, Portal, Dialog } from 'react-native-paper';
import { useTranslation } from 'react-i18next';


const debugLog = (...args: any[]) => {
  if (__DEV__) console.log(...args);
};

const { width: screenWidth } = Dimensions.get('window');
interface WeekPickerModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSelectWeek: (weekStart: string) => void;
  currentWeekStart?: string;
}

export const WeekPickerModal: React.FC<WeekPickerModalProps> = ({
  visible,
  onDismiss,
  onSelectWeek,
  currentWeekStart
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date>(
    currentWeekStart ? new Date(currentWeekStart) : new Date()
  );
  const [nativeDatePickerShow, setNativeDatePickerShow] = useState(false);
  const isDarkMode = theme.dark;
  const defaultStyles = useDefaultStyles();

  debugLog('WeekPickerModal: Component rendered, visible:', visible);
  debugLog('WeekPickerModal: currentWeekStart:', currentWeekStart);
  debugLog('WeekPickerModal: selectedDate:', selectedDate);

  // Theme colors for native pickers
  const androidAccentColor = theme.colors.primary;
  const androidBackgroundColor = isDarkMode ? '#333333' : '#FFFFFF';
  const androidHeaderBackgroundColor = theme.colors.primary;
  const androidHeaderTextColor = '#FFFFFF';
  const androidTextColor = isDarkMode ? '#FFFFFF' : '#000000';

  const themedStyles = {
    ...defaultStyles,
    day: {
      ...defaultStyles.day,
      backgroundColor: isDarkMode ? '#444' : '#f5f5f5'
    },
    today: {
      ...defaultStyles.today,
      borderColor: theme.colors.primary
    },
    selected: {
      ...defaultStyles.selected,
      backgroundColor: theme.colors.primary
    },
    selected_label: {
      ...defaultStyles.selected_label,
      color: '#fff'
    }
  };

  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    d.setDate(diff);
    return new Date(d);
  };

  const handleDateSelect = (date: Date) => {
    debugLog('WeekPickerModal: Date selected:', date);
    setSelectedDate(date);
    
    // Automatically select the week when a date is clicked
    const weekStart = getWeekStart(date);
    const weekStartString = weekStart.toISOString().split('T')[0];
    debugLog('WeekPickerModal: Calculated week start:', weekStart);
    debugLog('WeekPickerModal: Week start string:', weekStartString);
    debugLog('WeekPickerModal: Calling onSelectWeek with:', weekStartString);
    onSelectWeek(weekStartString);
    debugLog('WeekPickerModal: onSelectWeek called, dismissing modal');
    onDismiss();
  };

  const onNativeDateChange = (event: any, selectedDate?: Date) => {
    setNativeDatePickerShow(false);
    if (event.type === 'dismissed') return;
    if (selectedDate) {
      // Automatically select the week when a date is picked
      const weekStart = getWeekStart(selectedDate);
      const weekStartString = weekStart.toISOString().split('T')[0];
      debugLog('WeekPickerModal: Native date selected:', selectedDate);
      debugLog('WeekPickerModal: Calculated week start:', weekStart);
      debugLog('WeekPickerModal: Week start string:', weekStartString);
      debugLog('WeekPickerModal: Calling onSelectWeek with:', weekStartString);
      onSelectWeek(weekStartString);
      debugLog('WeekPickerModal: onSelectWeek called, dismissing modal');
      onDismiss();
    }
  };

  const formatWeekRange = (date: Date): string => {
    const weekStart = getWeekStart(date);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    const startStr = weekStart.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const endStr = weekEnd.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    return `${startStr} - ${endStr}`;
  };

  const renderWebCalendar = () => {
    debugLog('WeekPickerModal: renderWebCalendar called, visible:', visible);
    return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onDismiss}
    >
      <TouchableWithoutFeedback onPress={onDismiss}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>
            <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
              <Text variant="headlineSmall" style={[styles.modalTitle, { color: theme.colors.onSurface }]}>
                {t('selectWeek', 'Select Week')}
              </Text>
              
              <Text variant="bodyMedium" style={[styles.selectedWeekText, { color: theme.colors.onSurfaceVariant }]}>
                {t('clickDateToSelectWeek', 'Click any date to select that week')}
              </Text>

              <DateTimePicker
                mode="single"
                date={selectedDate}
                onChange={({ date }) => {
                  debugLog('WeekPickerModal: DateTimePicker onChange called with:', date);
                  if (date) {
                    // The library returns either a string, Date or timestamp
                    const dateObj = typeof date === 'string'
                      ? new Date(date)
                      : date instanceof Date
                        ? date
                        : typeof date === 'number'
                          ? new Date(date)
                          : null;
                    
                    if (dateObj) {
                      debugLog('WeekPickerModal: Processed date object:', dateObj);
                      handleDateSelect(dateObj);
                    }
                  }
                }}
                maxDate={new Date()}
                style={{ backgroundColor: isDarkMode ? '#333' : '#fff' }}
                styles={themedStyles}
                locale="en"
              />

              <View style={styles.buttonContainer}>
                <Button
                  mode="outlined"
                  onPress={onDismiss}
                  style={[styles.button, { borderColor: theme.colors.outline }]}
                  textColor={theme.colors.onSurface}
                >
                  {t('cancel', 'Cancel')}
                </Button>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
    );
  };

  const renderMobileCalendar = () => {
    debugLog('WeekPickerModal: renderMobileCalendar called, visible:', visible);
    return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
        <Dialog.Title style={{ color: theme.colors.onSurface }}>
          {t('selectWeek', 'Select Week')}
        </Dialog.Title>
        <Dialog.Content>
          <Text variant="bodyMedium" style={[styles.selectedWeekText, { color: theme.colors.onSurfaceVariant }]}>
            {t('clickDateToSelectWeek', 'Click any date to select that week')}
          </Text>
          
          {nativeDatePickerShow && (
            <NativeDateTimePicker
              value={selectedDate}
              mode="date"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={onNativeDateChange}
              maximumDate={new Date()}
              accentColor={theme.colors.primary}
              themeVariant={isDarkMode ? 'dark' : 'light'}
              textColor={isDarkMode ? '#FFFFFF' : '#000000'}
              style={{ backgroundColor: Platform.OS === 'android' ? androidBackgroundColor : undefined }}
              {...(Platform.OS === 'android'
                ? {
                    theme: {
                      backgroundColor: androidBackgroundColor,
                      headerBackgroundColor: androidHeaderBackgroundColor,
                      headerTextColor: androidHeaderTextColor,
                      textColor: androidTextColor,
                      accentColor: androidAccentColor,
                      calendarTextColor: androidTextColor,
                      calendarBackgroundColor: androidBackgroundColor,
                      dayColor: androidTextColor,
                      selectedDayColor: '#FFFFFF',
                      selectedDayBackgroundColor: androidAccentColor
                    }
                  }
                : {})}
            />
          )}
          
          {!nativeDatePickerShow && (
            <Button
              mode="outlined"
              onPress={() => setNativeDatePickerShow(true)}
              style={styles.openPickerButton}
              icon="calendar"
            >
              {t('selectDate', 'Select Date')}
            </Button>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss}>
            {t('cancel', 'Cancel')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
    );
  };

  debugLog('WeekPickerModal: About to render, Platform.OS:', Platform.OS);
  debugLog('WeekPickerModal: visible:', visible);
  
  return Platform.OS === 'web' ? renderWebCalendar() : renderMobileCalendar();
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    borderRadius: 12,
    padding: 20,
    width: screenWidth > 600 ? 400 : screenWidth - 40,
    maxWidth: 400,
    maxHeight: '80%',
  },
  modalTitle: {
    textAlign: 'center',
    marginBottom: 16,
    fontWeight: '600',
  },
  selectedWeekText: {
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: '500',
  },
  calendar: {
    marginBottom: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  button: {
    flex: 1,
  },
  dialog: {
    maxWidth: 400,
    alignSelf: 'center',
  },
  openPickerButton: {
    marginTop: 16,
  },
});
